import React, { useState } from "react";
import Icons from "../icons";
import Loader from "./Loader";
import { XrayTestSteps, scenarioCount } from "./TestCaseRow";
import { ChecksBadge, ChecksList } from "./AppPageChecks";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const GENERATION_LABEL = {
  passed: "Verified in the browser",
  failed: "Generated with issues",
  blocked: "Page did not open",
  error: "Error",
};
/* An Artifact the AppPage QA Agent wrote: it was written, not run. */
const AGENT_LABEL = {
  passed: "Written by the AppPage QA Agent",
  partial: "Written by the AppPage QA Agent (whole file)",
};

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/* Review: every Test of an App Page with its Cucumber scenario and, when
   automated, who made its Artifact and when — like the Requirement Board's
   Review. The full Test is fetched when its card is opened. */
export default function AppPageReview({ appPage, tests, onBack, onCreateArtifact }) {
  const [open, setOpen] = useState(() => new Set());
  const [detail, setDetail] = useState({}); // test_key -> {loading, data, error}

  const load = async (key) => {
    setDetail((d) => ({ ...d, [key]: { loading: true } }));
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/tests/${encodeURIComponent(key)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setDetail((d) => ({ ...d, [key]: { data: body.data } }));
    } catch (e) {
      setDetail((d) => ({ ...d, [key]: { error: e.message } }));
    }
  };

  const toggle = (key) => {
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!detail[key]) load(key);
  };
  const allOpen = tests.length > 0 && open.size === tests.length;
  const toggleAll = () => {
    if (allOpen) {
      setOpen(new Set());
      return;
    }
    setOpen(new Set(tests.map((t) => t.test_key)));
    tests.filter((t) => !detail[t.test_key]).forEach((t) => load(t.test_key));
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Icons.Grid className="h-6 w-6 text-blue-600" />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{appPage}</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {tests.length} tests · {tests.filter((t) => t.automated).length} automated
          </p>
        </div>
        {tests.length > 1 && (
          <button type="button" onClick={toggleAll} className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100">
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>

      <ul className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto">
        {tests.map((t) => {
          const isOpen = open.has(t.test_key);
          const d = detail[t.test_key] || {};
          const artifact = d.data?.artifact;
          const generation = artifact?.generation || {};
          return (
            <li key={t.test_key} className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
              <button
                type="button"
                onClick={() => toggle(t.test_key)}
                aria-expanded={isOpen}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-3 text-left transition hover:bg-blue-50/40"
              >
                <Icons.ChevronRight
                  className={["h-3.5 w-3.5 flex-none text-slate-400 transition-transform", isOpen ? "rotate-90" : ""].join(" ")}
                />
                <span className="text-sm font-medium text-blue-600">{t.test_key}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800" title={t.title}>
                  {t.title}
                </span>
                <span className="text-[0.6875rem] text-slate-400">{scenarioCount(t.steps)} steps</span>
                {t.status === "retired" && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">Retired</span>
                )}
                {t.drifted && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">steps changed</span>
                )}
                <ChecksBadge report={t.verification} />
                {t.automated ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                    <Icons.Check className="h-3.5 w-3.5" /> Automated
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Not automated</span>
                )}
              </button>
              {isOpen && (
                <div className="space-y-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                  {d.loading && (
                    <Loader.Overlay variant="guide" label="Loading the test…" sublabel={`Fetching ${t.test_key}`} delay={0} />
                  )}
                  {d.error && <p className="text-sm text-red-600">Could not load this test: {d.error}</p>}
                  {d.data && !artifact && (
                    <>
                      <XrayTestSteps steps={d.data.steps} />
                      {d.data.verification && <ChecksList report={d.data.verification} title="Checks of the scenario" />}
                      <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-card px-3 py-2.5">
                        <p className="min-w-0 flex-1 text-sm text-slate-500">No artifact automates this test yet.</p>
                        {t.status === "active" && (
                          <button
                            type="button"
                            onClick={() => onCreateArtifact?.([t.test_key])}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3 py-1.5 text-[0.8125rem] font-semibold text-white hover:bg-blue-700"
                          >
                            <Icons.Plus className="h-4 w-4" /> Create Artifact
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {/* As on the Requirement Board's Review: the Cucumber
                      scenario and who made its Artifact. The script itself is
                      stored, and shown on App Pages › Executions. */}
                  {d.data && artifact && (
                    <>
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                        <Icons.ScrollText className="h-3.5 w-3.5 text-brand-600" />
                        <span>
                          Artifact{artifact.created_by ? ` by ${artifact.created_by}` : ""}
                          {artifact.modifieddate ? ` · ${when(artifact.modifieddate)}` : ""}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-px font-semibold text-slate-600">
                          {(generation.engine === "agent" ? AGENT_LABEL : GENERATION_LABEL)[generation.status] ||
                            generation.status ||
                            "Generated"}
                        </span>
                        <span>{artifact.steps_count} script steps</span>
                        {artifact.versions > 0 && (
                          <span>· {artifact.versions} previous version{artifact.versions === 1 ? "" : "s"}</span>
                        )}
                        <ChecksBadge report={artifact.verification} what="script" label="Script" />
                      </p>
                      <XrayTestSteps steps={d.data.steps} />
                      {d.data.verification && <ChecksList report={d.data.verification} title="Checks of the scenario" />}
                      {artifact.verification && (
                        <ChecksList report={artifact.verification} title="Checks of the Playwright script" />
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Back
        </button>
      </div>
    </div>
  );
}
